import React, { useContext, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { VendorState } from '../../../../redux/vendor/rootReducer';
import { ColorModeContext } from '../../../../App';
import { borderRadius, typography } from '../../../../components/design-system/theme';
import copyrightLogo from '../../../../assets/copyright_logo.webp';

export type FocusRegion = 'overview' | 'header' | 'filters' | 'product-card';

type Slide = 'space-picker' | 'visualizer';

interface CardItemConfig {
    key: string;
    style?: string;
    overlay?: string;
    concatWithPrevious?: boolean;
    separator?: string;
    badgeBgColor?: string;
    badgeTextColor?: string;
}

interface VisualizerPreviewProps {
    focusRegion?: FocusRegion;
}

const LABELS = {
    en: {
        subtitle: 'Choose a space to visualize',
        filterByType: 'Filter by type',
        allRooms: 'All rooms',
        surface: 'Surface',
        floor: 'Floor',
        walls: 'Walls',
        upload: 'Upload your space',
        fromMobile: 'From mobile',
        add: 'Add',
        cart: 'Cart',
        favorites: 'Favorites',
        search: 'Search products',
        changeSpace: 'Change space',
        share: 'Share',
        download: 'Download',
        filters: 'Filters',
        color: 'Color',
        size: 'Size',
        reset: 'Reset',
        confirm: 'Confirm',
        showSelectedOnly: 'Show selected only',
    },
    fr: {
        subtitle: 'Choisissez un espace à visualiser',
        filterByType: 'Filtrer par type',
        allRooms: 'Toutes les pièces',
        surface: 'Surface',
        floor: 'Sol',
        walls: 'Murs',
        upload: 'Téléchargez votre espace',
        fromMobile: 'Depuis mobile',
        add: 'Ajouter',
        cart: 'Panier',
        favorites: 'Favoris',
        search: 'Rechercher',
        changeSpace: "Changer d'espace",
        share: 'Partager',
        download: 'Télécharger',
        filters: 'Filtres',
        color: 'Couleur',
        size: 'Taille',
        reset: 'Réinitialiser',
        confirm: 'Confirmer',
        showSelectedOnly: 'Afficher uniquement la sélection',
    },
    es: {
        subtitle: 'Elige un espacio para visualizar',
        filterByType: 'Filtrar por tipo',
        allRooms: 'Todas las habitaciones',
        surface: 'Superficie',
        floor: 'Suelo',
        walls: 'Paredes',
        upload: 'Sube tu espacio',
        fromMobile: 'Desde móvil',
        add: 'Añadir',
        cart: 'Carrito',
        favorites: 'Favoritos',
        search: 'Buscar productos',
        changeSpace: 'Cambiar espacio',
        share: 'Compartir',
        download: 'Descargar',
        filters: 'Filtros',
        color: 'Color',
        size: 'Tamaño',
        reset: 'Restablecer',
        confirm: 'Confirmar',
        showSelectedOnly: 'Mostrar solo seleccionados',
    },
    ar: {
        subtitle: 'اختر مساحة للعرض',
        filterByType: 'تصفية حسب النوع',
        allRooms: 'كل الغرف',
        surface: 'السطح',
        floor: 'الأرضية',
        walls: 'الجدران',
        upload: 'حمّل مساحتك',
        fromMobile: 'من الجوال',
        add: 'إضافة',
        cart: 'السلة',
        favorites: 'المفضلة',
        search: 'بحث',
        changeSpace: 'تغيير المساحة',
        share: 'مشاركة',
        download: 'تنزيل',
        filters: 'الفلاتر',
        color: 'اللون',
        size: 'الحجم',
        reset: 'إعادة',
        confirm: 'تأكيد',
        showSelectedOnly: 'إظهار المحدد فقط',
    },
} as const;

type LabelSet = typeof LABELS[keyof typeof LABELS];

// ── Preview palette ─────────────────────────────────────────────────────────
// The visualizer preview is a heavily designed mock with many surface,
// border, and text colours. Rather than scatter `mode === 'dark'` checks
// everywhere, we derive a single palette object once and thread it down.

interface PreviewPalette {
    cardBg: string;
    surfaceBg: string;        // sidebar background, outer frame
    mutedSurface: string;     // secondary surface (e.g. visualizer slide bg)
    canvasBg: string;         // 3D scene placeholder colour
    borderSoft: string;       // light separator (between sections)
    borderHard: string;       // input / chip border
    textHeading: string;      // primary text
    textBody: string;         // secondary text
    textMuted: string;        // muted / placeholder text
    chipBg: string;           // input + chip fill
    overlayGlass: string;     // glass overlays (top action bar)
    shimmerBase: string;
    shimmerHighlight: string;
    dropOverlay: string;      // dim layer behind filter drawer
}

const lightPalette: PreviewPalette = {
    cardBg: '#FFFFFF',
    surfaceBg: '#FFFFFF',
    mutedSurface: '#F8F9FA',
    canvasBg: '#ECEEF1',
    borderSoft: '#F3F4F6',
    borderHard: '#E5E7EB',
    textHeading: '#111827',
    textBody: '#374151',
    textMuted: '#6B7280',
    chipBg: '#FFFFFF',
    overlayGlass: 'rgba(255,255,255,0.85)',
    shimmerBase: '#F3F4F6',
    shimmerHighlight: '#E5E7EB',
    dropOverlay: 'rgba(0,0,0,0.25)',
};

const darkPalette: PreviewPalette = {
    cardBg: '#1F2937',
    surfaceBg: '#111827',
    mutedSurface: '#0F1721',
    canvasBg: '#1A2332',
    borderSoft: '#1F2937',
    borderHard: '#374151',
    textHeading: '#F9FAFB',
    textBody: '#D1D5DB',
    textMuted: '#9CA3AF',
    chipBg: '#1F2937',
    overlayGlass: 'rgba(31,41,55,0.85)',
    shimmerBase: '#1F2937',
    shimmerHighlight: '#374151',
    dropOverlay: 'rgba(0,0,0,0.5)',
};

interface SlideTheme {
    primary: string;
    textSecondary: string;
    logo?: string;
    company: string;
    labels: LabelSet;
    tileRadiusPx: string;
    buttonRadiusPx: string;
    orderButtonLabel: string;
    showFilters?: boolean;
    activeFilters: string[];
    productCardFields: CardItemConfig[];
    showProductCardDetails?: boolean;
    palette: PreviewPalette;
}

interface StubProduct {
    name: string;
    brand: string;
    color: string;
    size: string;
    sku: string;
    description: string;
}

const STUB_PRODUCTS: StubProduct[] = [
    { name: 'Marble Tile', brand: 'Stone Studio', color: 'Carrara', size: '60×60 cm', sku: 'ST-1234', description: 'Premium marble' },
    { name: 'Oak Hardwood', brand: 'OakCo', color: 'Natural', size: '200×20 cm', sku: 'OK-2031', description: 'Solid oak plank' },
    { name: 'Mosaic Glass', brand: 'MosaicArt', color: 'Blue Sea', size: '30×30 cm', sku: 'MS-7782', description: 'Hand-cut glass' },
    { name: 'Travertine', brand: 'NatureStone', color: 'Beige', size: '40×40 cm', sku: 'NS-9981', description: 'Natural stone' },
];

// Different beige / warm-earth tones — used as flat thumbnail fills so the
// preview reads like real product imagery instead of grey shimmer.
const BEIGE_PALETTE = [
    '#F5E6D3', '#E8D4B8', '#D4B896', '#C4A47C', '#B89A6E', '#A38860',
    '#F0DEC4', '#E5D2B0', '#DCC9A8', '#CDB892', '#B7A283', '#9C8866',
    '#EFE0CB', '#DDC9AC', '#C8B18E', '#A99270', '#8C7558', '#F2E1CB',
];

const fieldValue = (key: string, p: StubProduct): string => {
    if (key === 'name') return p.name;
    if (key === 'brand') return p.brand;
    if (key === 'color') return p.color;
    if (key === 'size') return p.size;
    if (key === 'sku') return p.sku;
    if (key === 'description') return p.description;
    return key.startsWith('attr:') ? key.slice(5) : key;
};

const shimmerGradient = (palette: PreviewPalette) =>
    `linear-gradient(110deg, ${palette.shimmerBase} 30%, ${palette.shimmerHighlight} 50%, ${palette.shimmerBase} 70%)`;

const ShimmerTile: React.FC<{ aspectRatio?: string; delay?: number; radius?: string; palette: PreviewPalette }> = ({
    aspectRatio = '4 / 3',
    delay = 0,
    radius = '8px',
    palette,
}) => (
    <div
        style={{
            width: '100%',
            aspectRatio,
            borderRadius: radius,
            breakInside: 'avoid',
            marginBottom: 6,
            background: shimmerGradient(palette),
            backgroundSize: '200% 100%',
            animation: 'ss-shimmer 2.4s ease-in-out infinite',
            animationDelay: `${delay}s`,
        }}
    />
);

const ShimmerBar: React.FC<{ width?: string | number; height?: number; delay?: number; radius?: string; palette: PreviewPalette }> = ({
    width = '70%',
    height = 6,
    delay = 0,
    radius = '3px',
    palette,
}) => (
    <div
        style={{
            width,
            height,
            borderRadius: radius,
            background: shimmerGradient(palette),
            backgroundSize: '200% 100%',
            animation: 'ss-shimmer 2.4s ease-in-out infinite',
            animationDelay: `${delay}s`,
        }}
    />
);

/* ───────────────────────── SpacePicker slide ───────────────────────── */

const SpacePickerSlide: React.FC<SlideTheme> = ({
    primary,
    textSecondary,
    logo,
    company,
    labels,
    tileRadiusPx,
    buttonRadiusPx,
    palette,
}) => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: palette.surfaceBg }}>
        {/* Header — logo LEFT, title RIGHT */}
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                paddingLeft: '14px',
                paddingRight: '14px',
                paddingTop: '10px',
                paddingBottom: '10px',
                borderBottom: `1px solid ${palette.borderSoft}`,
                flexShrink: 0,
            }}
        >
            {logo ? (
                <img
                    src={logo}
                    alt=""
                    style={{ height: 26, maxWidth: 110, objectFit: 'contain', flexShrink: 0 }}
                />
            ) : (
                <div
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: primary,
                        flexShrink: 0,
                    }}
                />
            )}
            <div style={{ textAlign: 'right', minWidth: 0, flex: 1 }}>
                <div
                    style={{
                        fontFamily: typography.fontFamily,
                        fontSize: 11,
                        fontWeight: typography.fontWeight.semibold,
                        color: palette.textHeading,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {company}
                </div>
                <div
                    style={{
                        fontFamily: typography.fontFamily,
                        fontSize: 8,
                        color: palette.textMuted,
                        marginTop: '2px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {labels.subtitle}
                </div>
            </div>
        </div>

        {/* Filter pills row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: '14px', paddingRight: '14px', paddingTop: '10px', paddingBottom: '10px', flexShrink: 0 }}>
            <div style={{ fontFamily: typography.fontFamily, fontSize: 8, color: palette.textMuted }}>
                {labels.filterByType}
            </div>
            <div
                style={{
                    paddingLeft: 8,
                    paddingRight: 8,
                    paddingTop: 3.2,
                    paddingBottom: 3.2,
                    borderRadius: buttonRadiusPx,
                    border: `1px solid ${palette.borderHard}`,
                    backgroundColor: palette.chipBg,
                    fontFamily: typography.fontFamily,
                    fontSize: 8,
                    color: palette.textBody,
                }}
            >
                {labels.allRooms} ▾
            </div>
            <div style={{ width: 8 }} />
            <div style={{ fontFamily: typography.fontFamily, fontSize: 8, color: palette.textMuted }}>
                {labels.surface}
            </div>
            {[labels.floor, labels.walls].map((label) => (
                <div
                    key={label}
                    style={{
                        paddingLeft: 8,
                        paddingRight: 8,
                        paddingTop: 3.2,
                        paddingBottom: 3.2,
                        borderRadius: buttonRadiusPx,
                        border: `1px solid ${palette.borderHard}`,
                        backgroundColor: palette.chipBg,
                        fontFamily: typography.fontFamily,
                        fontSize: 8,
                        color: palette.textBody,
                    }}
                >
                    ✓ {label}
                </div>
            ))}
        </div>

        {/* Body — CSS columns masonry */}
        <div
            style={{
                paddingLeft: '14px',
                paddingRight: '14px',
                paddingBottom: '14px',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                columnCount: 4,
                columnGap: '6px',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    breakInside: 'avoid',
                    marginBottom: 6,
                }}
            >
                <div
                    style={{
                        height: 28,
                        borderRadius: buttonRadiusPx,
                        backgroundColor: primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: textSecondary,
                        fontFamily: typography.fontFamily,
                        gap: 4,
                        paddingLeft: 6,
                        paddingRight: 6,
                    }}
                >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="6" width="18" height="14" rx="2" />
                        <circle cx="12" cy="13" r="3.5" />
                        <path d="M9 6l1.5-2h3L15 6" />
                    </svg>
                    <div
                        style={{
                            fontSize: 7.5,
                            fontWeight: typography.fontWeight.semibold,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {labels.upload}
                    </div>
                </div>
                <div
                    style={{
                        height: 22,
                        borderRadius: buttonRadiusPx,
                        border: `1px solid ${palette.borderHard}`,
                        backgroundColor: palette.chipBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                        paddingLeft: 6,
                        paddingRight: 6,
                    }}
                >
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={palette.textMuted} strokeWidth="1.5">
                        <rect x="3" y="3" width="6" height="6" />
                        <rect x="15" y="3" width="6" height="6" />
                        <rect x="3" y="15" width="6" height="6" />
                    </svg>
                    <div
                        style={{
                            fontFamily: typography.fontFamily,
                            fontSize: 7,
                            fontWeight: typography.fontWeight.medium,
                            color: palette.textBody,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {labels.fromMobile}
                    </div>
                </div>
            </div>

            <ShimmerTile aspectRatio="4 / 3" delay={0.0} radius={tileRadiusPx} palette={palette} />
            <ShimmerTile aspectRatio="3 / 4" delay={0.2} radius={tileRadiusPx} palette={palette} />
            <ShimmerTile aspectRatio="1 / 1" delay={0.4} radius={tileRadiusPx} palette={palette} />
            <ShimmerTile aspectRatio="4 / 3" delay={0.6} radius={tileRadiusPx} palette={palette} />
            <ShimmerTile aspectRatio="3 / 4" delay={0.8} radius={tileRadiusPx} palette={palette} />
            <ShimmerTile aspectRatio="4 / 3" delay={1.0} radius={tileRadiusPx} palette={palette} />
            <ShimmerTile aspectRatio="1 / 1" delay={1.2} radius={tileRadiusPx} palette={palette} />
            <ShimmerTile aspectRatio="4 / 3" delay={1.4} radius={tileRadiusPx} palette={palette} />
            <ShimmerTile aspectRatio="3 / 4" delay={1.6} radius={tileRadiusPx} palette={palette} />
            <ShimmerTile aspectRatio="4 / 3" delay={1.8} radius={tileRadiusPx} palette={palette} />
        </div>
    </div>
);

/* ───────────────────────── Visualizer slide ───────────────────────── */

const FILTER_COLORS = ['#1F2937', '#FFFFFF', '#9CA3AF', '#B45309', '#10B981', '#3B82F6', '#EF4444', '#F59E0B'];
const FILTER_SIZES = ['30×30', '60×60', '80×80', '20×120'];
const FILTER_GENERIC_VALUES: Record<string, string[]> = {
    Collection: ['Modern', 'Vintage', 'Classic', 'Industrial'],
    Family: ['Tile', 'Hardwood', 'Stone'],
    Pattern: ['Hex', 'Brick', 'Plain'],
};

/** Render the swatches/chips for a given filter name. Color → swatch grid;
 *  Size → numeric chips; everything else → generic text chips. */
const FilterSection: React.FC<{
    name: string;
    label: string;
    primary: string;
    textSecondary: string;
    tileRadiusPx: string;
    buttonRadiusPx: string;
    palette: PreviewPalette;
}> = ({ name, label, primary, textSecondary, tileRadiusPx, buttonRadiusPx, palette }) => {
    const isColor = name.toLowerCase() === 'color';
    const values = isColor
        ? FILTER_COLORS
        : name.toLowerCase() === 'size'
        ? FILTER_SIZES
        : FILTER_GENERIC_VALUES[name] ?? ['Option A', 'Option B', 'Option C'];

    return (
        <div className="viz-filter-row" style={{ paddingBottom: 6, marginBottom: 6, borderBottom: `1px solid ${palette.borderSoft}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 3.2, paddingBottom: 3.2 }}>
                <div
                    style={{
                        fontFamily: typography.fontFamily,
                        fontSize: 7,
                        fontWeight: typography.fontWeight.semibold,
                        color: palette.textHeading,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {label}
                </div>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={palette.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </div>
            {isColor ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '3px', marginTop: 3.2 }}>
                    {(values as string[]).map((c, i) => {
                        const isSelected = i === 0 || i === 5;
                        return (
                            <div
                                key={c}
                                style={{
                                    width: '100%',
                                    aspectRatio: '1 / 1',
                                    borderRadius: tileRadiusPx,
                                    backgroundColor: c,
                                    border: isSelected ? `1.5px solid ${primary}` : `1px solid ${palette.borderHard}`,
                                    position: 'relative',
                                }}
                            >
                                {isSelected && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '-2px',
                                            right: '-2px',
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: primary,
                                            color: textSecondary,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <svg width="4" height="4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3.2, marginTop: 3.2 }}>
                    {(values as string[]).map((s, i) => {
                        const isSelected = i === 1;
                        return (
                            <div
                                key={s}
                                style={{
                                    paddingLeft: 4,
                                    paddingRight: 4,
                                    paddingTop: '2px',
                                    paddingBottom: '2px',
                                    borderRadius: buttonRadiusPx,
                                    border: `1px solid ${isSelected ? primary : palette.borderHard}`,
                                    backgroundColor: isSelected ? `${primary}14` : 'transparent',
                                    fontFamily: typography.fontFamily,
                                    fontSize: 6,
                                    fontWeight: isSelected ? typography.fontWeight.semibold : typography.fontWeight.medium,
                                    color: isSelected ? palette.textHeading : palette.textBody,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {s}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const FilterDrawer: React.FC<{
    primary: string;
    textSecondary: string;
    labels: LabelSet;
    tileRadiusPx: string;
    buttonRadiusPx: string;
    activeFilters: string[];
    palette: PreviewPalette;
}> = ({ primary, textSecondary, labels, tileRadiusPx, buttonRadiusPx, activeFilters, palette }) => (
    <div
        style={{
            width: '100%',
            height: '100%',
            backgroundColor: palette.surfaceBg,
            borderRight: `1px solid ${palette.borderHard}`,
            boxShadow: '2px 0 8px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        {/* Header — count + show-selected toggle + close */}
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4.8,
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 6,
                paddingBottom: 6,
                borderBottom: `1px solid ${palette.borderSoft}`,
                flexShrink: 0,
            }}
        >
            <div
                style={{
                    fontFamily: typography.fontFamily,
                    fontSize: 7.5,
                    fontWeight: typography.fontWeight.semibold,
                    color: palette.textHeading,
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                3 {labels.filters}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3.2 }}>
                <div
                    style={{
                        fontFamily: typography.fontFamily,
                        fontSize: 6,
                        color: palette.textMuted,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {labels.showSelectedOnly}
                </div>
                {/* Mini switch */}
                <div
                    style={{
                        width: 14,
                        height: 8,
                        borderRadius: '999px',
                        backgroundColor: palette.borderHard,
                        position: 'relative',
                        flexShrink: 0,
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 1,
                            left: 1,
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: palette.cardBg,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                        }}
                    />
                </div>
            </div>
            {/* Close (X) */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 14,
                    height: 14,
                    color: palette.textMuted,
                    flexShrink: 0,
                }}
            >
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </div>
        </div>

        {/* Body — sections rendered in the order set by the vendor */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', paddingLeft: 8, paddingRight: 8, paddingTop: 6, paddingBottom: 6 }}>
            {activeFilters.map((f) => {
                const lower = f.toLowerCase();
                const sectionLabel = lower === 'color' ? labels.color : lower === 'size' ? labels.size : f;
                return (
                    <FilterSection
                        key={f}
                        name={f}
                        label={sectionLabel}
                        primary={primary}
                        textSecondary={textSecondary}
                        tileRadiusPx={tileRadiusPx}
                        buttonRadiusPx={buttonRadiusPx}
                        palette={palette}
                    />
                );
            })}
        </div>

        {/* Footer — Reset + Confirm */}
        <div
            style={{
                display: 'flex',
                gap: 4,
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 6,
                paddingBottom: 6,
                borderTop: `1px solid ${palette.borderSoft}`,
                flexShrink: 0,
            }}
        >
            <div
                style={{
                    flex: 1,
                    height: 18,
                    borderRadius: buttonRadiusPx,
                    border: `1px solid ${palette.borderHard}`,
                    backgroundColor: palette.chipBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: typography.fontFamily,
                    fontSize: 7,
                    fontWeight: typography.fontWeight.medium,
                    color: palette.textBody,
                }}
            >
                {labels.reset}
            </div>
            <div
                style={{
                    flex: 1,
                    height: 18,
                    borderRadius: buttonRadiusPx,
                    backgroundColor: primary,
                    color: textSecondary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: typography.fontFamily,
                    fontSize: 7,
                    fontWeight: typography.fontWeight.semibold,
                }}
            >
                {labels.confirm}
            </div>
        </div>
    </div>
);

const VisualizerSlide: React.FC<SlideTheme> = ({
    primary,
    textSecondary,
    logo,
    company,
    labels,
    tileRadiusPx,
    buttonRadiusPx,
    orderButtonLabel,
    showFilters,
    activeFilters,
    productCardFields,
    showProductCardDetails,
    palette,
}) => {
    const tabs: Array<{ key: string; label: string; icon: React.ReactNode }> = [
        {
            key: 'add',
            label: labels.add,
            icon: (
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
            ),
        },
        {
            key: 'cart',
            label: labels.cart,
            icon: (
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1" />
                    <circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
            ),
        },
        {
            key: 'favorites',
            label: labels.favorites,
            icon: (
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                </svg>
            ),
        },
    ];

    return (
        <div style={{ display: 'flex', height: '100%', backgroundColor: palette.mutedSurface, position: 'relative' }}>
            {/* Sidebar */}
            <div
                style={{
                    width: '32%',
                    borderRight: `1px solid ${palette.borderHard}`,
                    backgroundColor: palette.surfaceBg,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                }}
            >
                {/* Sidebar header — logo + Change Space pill */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 6,
                        paddingLeft: '10px',
                        paddingRight: '10px',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        borderBottom: `1px solid ${palette.borderSoft}`,
                        flexShrink: 0,
                    }}
                >
                    {logo ? (
                        <img
                            src={logo}
                            alt=""
                            style={{ height: 20, maxWidth: 80, objectFit: 'contain', flexShrink: 0 }}
                        />
                    ) : (
                        <div
                            style={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: primary,
                                flexShrink: 0,
                            }}
                        />
                    )}
                    {/* Change space + Collapse sidebar — icon-only, mirrors
                        WebSideBar headerButtonStyle */}
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 18,
                                height: 18,
                                borderRadius: buttonRadiusPx,
                                border: `1px solid ${palette.borderHard}`,
                                backgroundColor: palette.chipBg,
                                color: palette.textMuted,
                            }}
                        >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                <path d="M17.8333 18C20.1345 18 22 16.0972 22 13.75V5.25C22 2.90279 20.1345 1 17.8333 1H11.1667C8.86548 1 7 2.90279 7 5.25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                <path d="M2 10.25C2 7.90279 3.86548 6 6.16667 6H12.8333C15.1345 6 17 7.90279 17 10.25V18.75C17 21.0972 15.1345 23 12.8333 23H6.16667C3.86548 23 2 21.0972 2 18.75V10.25Z" stroke="currentColor" strokeWidth="1.8" />
                            </svg>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 18,
                                height: 18,
                                borderRadius: buttonRadiusPx,
                                border: `1px solid ${palette.borderHard}`,
                                backgroundColor: palette.chipBg,
                                color: palette.textMuted,
                            }}
                        >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                <path d="M4 4V20M8 12H20M8 12L13 7M8 12L13 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Tabs row — no background, sliding bottom underline */}
                <div
                    style={{
                        display: 'flex',
                        position: 'relative',
                        borderBottom: `1px solid ${palette.borderSoft}`,
                        flexShrink: 0,
                    }}
                >
                    {tabs.map((tab, idx) => {
                        const isActive = idx === 0;
                        return (
                            <div
                                key={tab.key}
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 3.2,
                                    paddingTop: '6px',
                                    paddingBottom: '6px',
                                    color: isActive ? palette.textHeading : palette.textMuted,
                                    fontFamily: typography.fontFamily,
                                    fontSize: 7.5,
                                    fontWeight: isActive ? typography.fontWeight.semibold : typography.fontWeight.medium,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {tab.icon}
                                {tab.label}
                            </div>
                        );
                    })}
                    {/* Sliding active indicator (active = 'add' = index 0) */}
                    <div
                        style={{
                            position: 'absolute',
                            bottom: -1,
                            left: 0,
                            width: `calc(100% / ${tabs.length})`,
                            height: '2px',
                            backgroundColor: palette.textHeading,
                            borderRadius: '2px 2px 0 0',
                        }}
                    />
                </div>

                {/* Burger + search + filters row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 3.2, paddingLeft: '10px', paddingRight: '10px', paddingTop: '8px', paddingBottom: '8px', flexShrink: 0 }}>
                    {/* Burger menu */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 18,
                            height: 18,
                            borderRadius: buttonRadiusPx,
                            border: `1px solid ${palette.borderHard}`,
                            backgroundColor: palette.chipBg,
                            color: palette.textMuted,
                            flexShrink: 0,
                        }}
                    >
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="3" y1="6" x2="21" y2="6" />
                            <line x1="3" y1="12" x2="21" y2="12" />
                            <line x1="3" y1="18" x2="21" y2="18" />
                        </svg>
                    </div>

                    {/* Search input */}
                    <div
                        style={{
                            flex: 1,
                            minWidth: 0,
                            height: 18,
                            borderRadius: buttonRadiusPx,
                            border: `1px solid ${palette.borderHard}`,
                            backgroundColor: palette.chipBg,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            paddingLeft: 6,
                            paddingRight: 6,
                        }}
                    >
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={palette.textMuted} strokeWidth="2">
                            <circle cx="11" cy="11" r="7" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <div
                            style={{
                                fontFamily: typography.fontFamily,
                                fontSize: 7,
                                color: palette.textMuted,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {labels.search}
                        </div>
                    </div>

                    {/* Filters */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 18,
                            height: 18,
                            borderRadius: buttonRadiusPx,
                            border: `1px solid ${palette.borderHard}`,
                            backgroundColor: palette.chipBg,
                            color: palette.textMuted,
                            flexShrink: 0,
                        }}
                    >
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                        </svg>
                    </div>
                </div>

                {/* Product cards grid — always 3 columns. In the product-card
                    focus we render only 6 cards (2 rows) so each card has
                    plenty of vertical room to show every configured field
                    under the thumbnail. Default skeleton mode keeps the
                    denser 18-card view. */}
                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                        paddingLeft: '10px',
                        paddingRight: '10px',
                        paddingBottom: '10px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 6,
                        alignContent: 'start',
                    }}
                >
                    {showProductCardDetails
                        ? Array.from({ length: 12 }).map((_, i) => (
                              <PreviewProductCard
                                  key={i}
                                  product={STUB_PRODUCTS[i % STUB_PRODUCTS.length]}
                                  fields={productCardFields}
                                  primary={primary}
                                  textSecondary={textSecondary}
                                  tileRadiusPx={tileRadiusPx}
                                  buttonRadiusPx={buttonRadiusPx}
                                  delay={(i % 6) * 0.15}
                                  thumbnailColor={BEIGE_PALETTE[i % BEIGE_PALETTE.length]}
                                  palette={palette}
                              />
                          ))
                        : Array.from({ length: 18 }).map((_, i) => {
                              const delay = (i % 9) * 0.15;
                              return (
                                  <div
                                      key={i}
                                      style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: 3.2,
                                          minWidth: 0,
                                      }}
                                  >
                                      <ShimmerTile aspectRatio="1 / 1" delay={delay} radius={tileRadiusPx} palette={palette} />
                                      <ShimmerBar width="75%" height={4} delay={delay} palette={palette} />
                                  </div>
                              );
                          })}
                </div>
            </div>

            {/* Canvas area */}
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    position: 'relative',
                    overflow: 'hidden',
                    containerType: 'inline-size',
                    backgroundColor: palette.canvasBg,
                }}
            >
                {/* Top action bar — applied product (left) + share/download
                    + Order CTA (right). Mirrors ai-visualizer's
                    ProductActionsBar at preview scale. */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 6,
                        paddingLeft: 8,
                        paddingRight: 8,
                        backgroundColor: palette.overlayGlass,
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        borderBottomLeftRadius: tileRadiusPx,
                        borderBottomRightRadius: tileRadiusPx,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        zIndex: 2,
                    }}
                >
                    {/* Applied product info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                width: 18,
                                height: 18,
                                borderRadius: '4px',
                                background: shimmerGradient(palette),
                                backgroundSize: '200% 100%',
                                animation: 'ss-shimmer 2.4s ease-in-out infinite',
                                flexShrink: 0,
                            }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                            <ShimmerBar width={32} height={3.5} delay={0} palette={palette} />
                            <ShimmerBar width={56} height={4.5} delay={0.15} palette={palette} />
                        </div>
                    </div>

                    {/* Right action group */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3.2, flexShrink: 0 }}>
                        {/* Share */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2.4,
                                height: 18,
                                paddingLeft: 4,
                                paddingRight: 4,
                                borderRadius: buttonRadiusPx,
                                color: palette.textMuted,
                                fontFamily: typography.fontFamily,
                                fontSize: 7,
                                fontWeight: typography.fontWeight.medium,
                            }}
                        >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="18" cy="5" r="3" />
                                <circle cx="6" cy="12" r="3" />
                                <circle cx="18" cy="19" r="3" />
                                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                            </svg>
                            <span
                                className="viz-hide-narrow"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {labels.share}
                            </span>
                        </div>
                        {/* Download */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2.4,
                                height: 18,
                                paddingLeft: 4,
                                paddingRight: 4,
                                borderRadius: buttonRadiusPx,
                                color: palette.textMuted,
                                fontFamily: typography.fontFamily,
                                fontSize: 7,
                                fontWeight: typography.fontWeight.medium,
                            }}
                        >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            <span
                                className="viz-hide-narrow"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {labels.download}
                            </span>
                        </div>
                        {/* Order CTA — outlined neutral, matches the real
                            ProductActionsBar order button style */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2.8,
                                height: 18,
                                paddingLeft: 6,
                                paddingRight: 6,
                                borderRadius: buttonRadiusPx,
                                backgroundColor: palette.chipBg,
                                border: `1px solid ${palette.borderHard}`,
                                color: palette.textBody,
                                fontFamily: typography.fontFamily,
                                fontSize: 7,
                                fontWeight: typography.fontWeight.semibold,
                                whiteSpace: 'nowrap',
                                maxWidth: 80,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="9" cy="21" r="1" />
                                <circle cx="20" cy="21" r="1" />
                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                            </svg>
                            <span
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    minWidth: 0,
                                }}
                            >
                                {orderButtonLabel}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Full-bleed scene shimmer */}
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: shimmerGradient(palette),
                        backgroundSize: '200% 100%',
                        animation: 'ss-shimmer 2.4s ease-in-out infinite',
                    }}
                />

                {/* Copyright label — "Powered by Ardealy" (mirrors the real
                    CopyrightLabel from ai-visualizer at scale) */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3.2,
                        paddingLeft: 4.8,
                        paddingRight: 4.8,
                        paddingTop: '2px',
                        paddingBottom: '2px',
                        borderRadius: '999px',
                        backgroundColor: palette.overlayGlass,
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                    }}
                >
                    <div
                        style={{
                            fontFamily: typography.fontFamily,
                            fontSize: 6.5,
                            color: palette.textBody,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Powered by
                    </div>
                    <img
                        src={copyrightLogo}
                        alt="Ardealy"
                        style={{ height: 8, width: 'auto', display: 'block' }}
                    />
                </div>
            </div>

            {/* Filter drawer overlay */}
            <AnimatePresence>
                {showFilters && (
                    <>
                        <motion.div
                            key="filter-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                backgroundColor: palette.dropOverlay,
                                zIndex: 3,
                            }}
                        />
                        <motion.div
                            key="filter-drawer"
                            initial={{ x: '-100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '-100%' }}
                            transition={{ duration: 0.28, ease: 'easeOut' }}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                width: '32%',
                                zIndex: 4,
                            }}
                        >
                            <FilterDrawer
                                primary={primary}
                                textSecondary={textSecondary}
                                labels={labels}
                                tileRadiusPx={tileRadiusPx}
                                buttonRadiusPx={buttonRadiusPx}
                                activeFilters={activeFilters}
                                palette={palette}
                            />
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

/* ───────────────────────── ProductCards slide ───────────────────────── */

/** Render one product card preview with the configured fields, styles, and
 *  overlays — mirrors WebProductCard from ai-visualizer at preview scale. */
const PreviewProductCard: React.FC<{
    product: StubProduct;
    fields: CardItemConfig[];
    primary: string;
    textSecondary: string;
    tileRadiusPx: string;
    buttonRadiusPx: string;
    delay: number;
    thumbnailColor?: string;
    palette: PreviewPalette;
}> = ({ product, fields, primary, textSecondary, tileRadiusPx, buttonRadiusPx, delay, thumbnailColor, palette }) => {
    const enabled = fields ?? [];
    const overlayItems = enabled.filter((f) => f.overlay && f.overlay !== 'none');
    const textItems = enabled.filter((f) => !f.overlay || f.overlay === 'none');

    // Group concat-with-previous fields onto the same row.
    const rows: CardItemConfig[][] = [];
    textItems.forEach((f, i) => {
        if (i === 0 || !f.concatWithPrevious) {
            rows.push([f]);
        } else {
            rows[rows.length - 1].push(f);
        }
    });

    const overlayPos = (overlay?: string): React.CSSProperties => {
        const base: React.CSSProperties = { position: 'absolute' as const };
        switch (overlay) {
            case 'top-left': return { ...base, top: 4, left: 4 };
            case 'top-right': return { ...base, top: 4, right: 4 };
            case 'bottom-left': return { ...base, bottom: 4, left: 4 };
            case 'bottom-right': return { ...base, bottom: 4, right: 4 };
            default: return base;
        }
    };

    const renderRow = (row: CardItemConfig[]) => {
        const first = row[0];
        const style = first.style ?? 'default';
        const text = row.map((f, idx) => {
            const value = fieldValue(f.key, product);
            if (idx === 0) return value;
            return `${f.separator ?? ', '}${value}`;
        }).join('');

        if (style === 'tag' || style === 'tag-outline') {
            const filled = style === 'tag';
            const bgColor = first.badgeBgColor || (filled ? primary : 'transparent');
            const textColor = first.badgeTextColor || (filled ? textSecondary : palette.textBody);
            const borderColor = first.badgeBgColor || (filled ? bgColor : palette.borderHard);
            return (
                <div
                    key={first.key}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        paddingLeft: 4.8,
                        paddingRight: 4.8,
                        paddingTop: '2px',
                        paddingBottom: '2px',
                        borderRadius: buttonRadiusPx,
                        backgroundColor: bgColor,
                        color: textColor,
                        border: `1px solid ${borderColor}`,
                        fontFamily: typography.fontFamily,
                        fontSize: 7,
                        fontWeight: typography.fontWeight.medium,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.25,
                        alignSelf: 'flex-start',
                        maxWidth: '100%',
                    }}
                >
                    {text}
                </div>
            );
        }

        const isBold = style === 'bold';
        const isMuted = style === 'muted';
        return (
            <div
                key={first.key}
                style={{
                    fontFamily: typography.fontFamily,
                    fontSize: isMuted ? 7 : 7.5,
                    fontWeight: isBold ? typography.fontWeight.bold : typography.fontWeight.medium,
                    color: isMuted ? palette.textMuted : palette.textHeading,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.3,
                }}
            >
                {text}
            </div>
        );
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: palette.cardBg,
                border: `1px solid ${palette.borderHard}`,
                borderRadius: tileRadiusPx,
                alignSelf: 'start',
                minHeight: 0,
            }}
        >
            {/* Image with overlays — uses overflow:hidden + matching top-radius
                so corners clip cleanly without forcing an overflow:hidden on
                the parent card (which would clip wrapped text rows). */}
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '4 / 3',
                    overflow: 'hidden',
                    borderTopLeftRadius: tileRadiusPx,
                    borderTopRightRadius: tileRadiusPx,
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: thumbnailColor ? thumbnailColor : shimmerGradient(palette),
                        backgroundSize: thumbnailColor ? undefined : '200% 100%',
                        animation: thumbnailColor ? undefined : 'ss-shimmer 2.4s ease-in-out infinite',
                        animationDelay: `${delay}s`,
                    }}
                />
                {overlayItems.map((f) => {
                    const value = fieldValue(f.key, product);
                    const filled = f.style !== 'tag-outline';
                    const bgColor = f.badgeBgColor || (filled ? primary : palette.overlayGlass);
                    const textColor = f.badgeTextColor || (filled ? textSecondary : palette.textHeading);
                    return (
                        <div
                            key={f.key}
                            style={{
                                paddingLeft: 4.8,
                                paddingRight: 4.8,
                                paddingTop: '2px',
                                paddingBottom: '2px',
                                borderRadius: buttonRadiusPx,
                                backgroundColor: bgColor,
                                color: textColor,
                                border: filled ? 'none' : `1px solid ${textColor}`,
                                fontFamily: typography.fontFamily,
                                fontSize: 7,
                                fontWeight: typography.fontWeight.semibold,
                                whiteSpace: 'nowrap',
                                maxWidth: '70%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                ...overlayPos(f.overlay),
                            }}
                        >
                            {value}
                        </div>
                    );
                })}
            </div>
            {/* Text rows */}
            {rows.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 6 }}>
                    {rows.map((row, i) => (
                        <React.Fragment key={i}>{renderRow(row)}</React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ───────────────────────── Wrapper with slide nav ───────────────────────── */

const NavArrow: React.FC<{
    side: 'left' | 'right';
    onClick: () => void;
}> = ({ side, onClick }) => (
    <div
        onClick={onClick}
        // The arrows are this app's chrome around the preview, not part of the
        // vendor's branded miniature — so they take the app's own tokens.
        className="viz-arrow"
        style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            [side]: 6,
            width: 22,
            height: 22,
            borderRadius: '50%',
            backgroundColor: 'var(--card)',
            border: '1px solid var(--border)',
            color: 'var(--foreground)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            zIndex: 5,
        }}
    >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            {side === 'left' ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
        </svg>
    </div>
);

export const VisualizerPreview: React.FC<VisualizerPreviewProps> = ({ focusRegion = 'overview' }) => {
    const { mode } = useContext(ColorModeContext);
    const isDarkMode = mode === 'dark';
    const palette = isDarkMode ? darkPalette : lightPalette;
    const theme = useSelector((state: VendorState) => state.settings.theme);
    const [slide, setSlide] = useState<Slide>('space-picker');

    const primary = theme?.primaryColor || '#3B82F6';
    const textSecondary = theme?.textSecondaryColor || '#FFFFFF';
    const logo = theme?.logo;
    const company = theme?.vendorName || 'My Brand';
    const lang = (theme?.language || 'en') as keyof typeof LABELS;
    const labels = LABELS[lang] ?? LABELS.en;
    const isRTL = lang === 'ar';
    const radius = parseInt(theme?.cornerRadius || '8', 10);
    const tileRadiusPx = `${Math.round(radius * 0.5)}px`;
    const buttonRadiusPx = `${Math.min(Math.round(radius * 0.5), 10)}px`;
    const orderButtonLabel = theme?.orderButtonLabel || 'Order';
    const activeFilters = theme?.visualizerFilters && theme.visualizerFilters.length > 0
        ? theme.visualizerFilters
        : ['Color', 'Size', 'Collection'];
    const productCardFields = theme?.productCardFields && theme.productCardFields.length > 0
        ? theme.productCardFields
        : [{ key: 'name' }, { key: 'brand' }, { key: 'color' }, { key: 'size' }];

    // Sync the active slide with the parent tab — Advanced and Product
    // Display both live on the Visualizer slide; the focusRegion further
    // toggles the filter drawer or the product-card zoom-in.
    useEffect(() => {
        if (focusRegion === 'filters' || focusRegion === 'product-card') {
            setSlide('visualizer');
        }
    }, [focusRegion]);

    const showFilters = focusRegion === 'filters' && slide === 'visualizer';
    const showProductCardDetails = focusRegion === 'product-card' && slide === 'visualizer';

    const slideProps: SlideTheme = {
        primary,
        textSecondary,
        logo,
        company,
        labels,
        tileRadiusPx,
        buttonRadiusPx,
        orderButtonLabel,
        showFilters,
        activeFilters,
        productCardFields,
        showProductCardDetails,
        palette,
    };

    const slides: Slide[] = ['space-picker', 'visualizer'];
    const slideIndex = slides.indexOf(slide);
    const goPrev = () => slideIndex > 0 && setSlide(slides[slideIndex - 1]);
    const goNext = () => slideIndex < slides.length - 1 && setSlide(slides[slideIndex + 1]);
    const direction = slide === 'visualizer' ? 1 : -1;

    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '4 / 3',
                borderRadius: borderRadius.md,
                border: '1px solid var(--border)',
                backgroundColor: palette.surfaceBg,
                overflow: 'hidden',
                userSelect: 'none',
                direction: isRTL ? 'rtl' : 'ltr',
            }}
        >
            <AnimatePresence initial={false} custom={direction} mode="popLayout">
                <motion.div
                    key={slide}
                    custom={direction}
                    initial={{ opacity: 0, x: direction * 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -direction * 30 }}
                    transition={{ duration: 0.28, ease: 'easeOut' }}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {slide === 'space-picker' && <SpacePickerSlide {...slideProps} />}
                    {slide === 'visualizer' && <VisualizerSlide {...slideProps} />}
                </motion.div>
            </AnimatePresence>

            {slideIndex > 0 && (
                <NavArrow
                    side="left"
                    onClick={goPrev}
                />
            )}
            {slideIndex < slides.length - 1 && (
                <NavArrow
                    side="right"
                    onClick={goNext}
                />
            )}

            {/* Slide indicator dots */}
            <div
                style={{
                    position: 'absolute',
                    bottom: 6,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 4,
                    zIndex: 5,
                }}
            >
                {slides.map((s) => (
                    <div
                        key={s}
                        onClick={() => setSlide(s)}
                        style={{
                            width: s === slide ? 14 : 6,
                            height: 6,
                            borderRadius: '999px',
                            backgroundColor: s === slide ? primary : 'rgba(0,0,0,0.18)',
                            cursor: 'pointer',
                            transition: 'width 0.2s ease, background-color 0.2s ease',
                        }}
                    />
                ))}
            </div>
        </div>
    );
};
